import { ReactElement, useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Stack, Button, TextField, InputAdornment } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { NumberInput } from '@ui/forms'
import { CollapsibleDivider } from '@ui/structure'

const XYZTranslationFields = (): ReactElement => {
  const { setValue, formState, reset } = useFormContext()

  // Local state for XYZ translation values
  const [xTranslation, setXTranslation] = useState<number>(0)
  const [yTranslation, setYTranslation] = useState<number>(0)
  const [zTranslation, setZTranslation] = useState<number>(0)

  // Function to reset translation values
  const resetTranslation = useCallback(() => {
    setXTranslation(0)
    setYTranslation(0)
    setZTranslation(0)
  }, [])

  // Function to apply XYZ translation
  const handleClickApply = useCallback(() => {
    const originalStart = formState.defaultValues?.start as ImmutableVector3
    const originalEnd = formState.defaultValues?.end as ImmutableVector3

    if (!originalStart || !originalEnd) return

    // Apply translation to both start and end points
    const newStart = new ImmutableVector3(
      originalStart.x + xTranslation,
      originalStart.y + yTranslation,
      originalStart.z + zTranslation,
    )

    const newEnd = new ImmutableVector3(
      originalEnd.x + xTranslation,
      originalEnd.y + yTranslation,
      originalEnd.z + zTranslation,
    )

    // Update the form values
    setValue('start', newStart)
    setValue('end', newEnd)
    resetTranslation()
  }, [
    xTranslation,
    yTranslation,
    zTranslation,
    setValue,
    formState.defaultValues,
    resetTranslation,
  ])

  const handleClickReset = useCallback(() => {
    resetTranslation()
    reset()
  }, [resetTranslation, reset])

  return (
    <CollapsibleDivider text="verschieben (XYZ)" defaultExpanded={false}>
      <Stack direction="column" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <TextField
              variant="outlined"
              label="X"
              size="small"
              placeholder="0,00"
              value={xTranslation || ''}
              onChange={event => {
                const value = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
                setXTranslation(value)
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                inputComponent: NumberInput as any,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems={'center'}>
            <TextField
              variant="outlined"
              label="Y"
              size="small"
              placeholder="0,00"
              value={yTranslation || ''}
              onChange={event => {
                const value = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
                setYTranslation(value)
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                inputComponent: NumberInput as any,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems={'center'}>
            <TextField
              label="Z"
              variant="outlined"
              size="small"
              placeholder="0,00"
              value={zTranslation || ''}
              onChange={event => {
                const value = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
                setZTranslation(value)
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                inputComponent: NumberInput as any,
              }}
            />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleClickApply}
            disabled={xTranslation === 0 && yTranslation === 0 && zTranslation === 0}
            fullWidth
          >
            Anwenden
          </Button>
          <Button size="small" onClick={handleClickReset} fullWidth variant="outlined">
            Zurücksetzen
          </Button>
        </Stack>
      </Stack>
    </CollapsibleDivider>
  )
}

export default XYZTranslationFields
